import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { URL_RESET_SEEN_COUNT } from '@/notifications/lib/notification-constants';
import { UNREAD_NOTIFICATION_COUNT_KEY } from './use-unread-notification-count';

/**
 * Reset the user's "unseen notifications" counter to zero — fires when
 * the Notification Center panel opens (Flutter
 * `notification_center_screen.dart:45`).
 *
 *   POST /api/Notifications/ResetNotificationSeenCount
 *   body: empty
 *
 * Mirrors Flutter `notification_center_data_source_impl.dart:63-72`.
 *
 * **Note on the seen vs read distinction**: "seen" is the badge dot —
 * cleared by this endpoint. "read" is the per-row dimming — cleared by
 * `MarkNotificationAsRead`. Two separate counters; opening the panel
 * clears "seen" so the badge disappears, but rows stay "unread" until
 * the user actually scrolls past them in the panel.
 *
 * **Optimistic update**: drop the badge to 0 immediately via
 * `qc.setQueryData`. SignalR will confirm with the next push (or stay
 * silent if backend doesn't re-push for self-initiated resets).
 */

export function useResetNotificationSeenCount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<unknown>(URL_RESET_SEEN_COUNT, undefined),
    onSuccess: () => {
      // Drop badge to 0 immediately. SignalR may re-broadcast 0 on the
      // server-side or stay silent — either way our cache is correct.
      qc.setQueryData(UNREAD_NOTIFICATION_COUNT_KEY, 0);
    },
  });
}
